import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';

import consumerSpecializationPageQuery from 'bundles/unified-description-page-common/queries/bff/ConsumerSpecializationPageQuery.graphql';
import enterpriseOrgScopedSpecializationPageQuery from 'bundles/unified-description-page-common/queries/bff/EnterpriseOrgScopedSpecializationPageQuery.graphql';
import enterpriseSpecializationPageQuery from 'bundles/unified-description-page-common/queries/bff/EnterpriseSpecializationPageQuery.graphql';
import type {
  ConsumerSpecializationPageFieldsFragment,
  ConsumerSpecializationPageQuery as ConsumerSpecializationPageQueryResult,
  ConsumerSpecializationPageQueryVariables,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type {
  EnterpriseOrgScopedSpecializationPageFieldsFragment,
  EnterpriseOrgScopedSpecializationPageQuery as EnterpriseOrgScopedSpecializationPageQueryResult,
  EnterpriseOrgScopedSpecializationPageQueryVariables,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseOrgScopedSpecializationPageQuery';
import type {
  EnterpriseSpecializationPageFieldsFragment,
  EnterpriseSpecializationPageQuery as EnterpriseSpecializationPageQueryResult,
  EnterpriseSpecializationPageQueryVariables,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';

export type SpecializationReviewsPageData =
  ConsumerSpecializationPageFieldsFragment | EnterpriseSpecializationPageFieldsFragment;

type RouteDataResult = {
  loading: boolean;
  error?: Error;
  notFound: boolean;
  pageData: SpecializationReviewsPageData | null;
  isEnterprise: boolean;
};

/**
 * Page-level data hook for the standalone specialization/professional-certificate reviews page.
 * Runs a fresh, direct query against the same 3 GraphQL queries the specialization/PC XDP pages
 * already use (consumer, program-scoped enterprise, org-scoped enterprise) -- rather than reusing
 * the XDP page's heavier provider tree (campaign modules, ownership data, coach providers, etc.
 * that this page doesn't need). Professional certificates are a specialization under the hood, so
 * the same queries/fragments serve both product types (see `professionalCertificateType` on the
 * result for the discriminator).
 */
export function useSpecializationReviewsRouteData(): RouteDataResult {
  const params = useParams();
  const slug = params?.slug ?? '';
  const programSlug = params?.programSlug;
  const orgSlug = params?.orgSlug;

  const isOrgScoped = !!orgSlug;
  const isProgramScoped = !isOrgScoped && !!programSlug;
  const isEnterprise = isOrgScoped || isProgramScoped;

  const consumerResult = useQuery<ConsumerSpecializationPageQueryResult, ConsumerSpecializationPageQueryVariables>(
    consumerSpecializationPageQuery,
    {
      variables: { slug },
      skip: !slug || isEnterprise,
      context: { clientName: 'gatewayGql' },
    }
  );

  const enterpriseResult = useQuery<
    EnterpriseSpecializationPageQueryResult,
    EnterpriseSpecializationPageQueryVariables
  >(enterpriseSpecializationPageQuery, {
    variables: { slug, programSlug: programSlug ?? '' },
    skip: !slug || !isProgramScoped,
    context: { clientName: 'gatewayGql' },
  });

  const enterpriseOrgScopedResult = useQuery<
    EnterpriseOrgScopedSpecializationPageQueryResult,
    EnterpriseOrgScopedSpecializationPageQueryVariables
  >(enterpriseOrgScopedSpecializationPageQuery, {
    variables: { slug, organizationSlug: orgSlug ?? '' },
    skip: !slug || !isOrgScoped,
    context: { clientName: 'gatewayGql' },
  });

  let pageData: SpecializationReviewsPageData | null = null;
  let loading: boolean;
  let error: Error | undefined;

  if (isOrgScoped) {
    loading = enterpriseOrgScopedResult.loading;
    error = enterpriseOrgScopedResult.error;
    pageData =
      (enterpriseOrgScopedResult.data?.DescriptionPage?.queryEnterpriseSpecializationPageByOrg as
        | (EnterpriseOrgScopedSpecializationPageFieldsFragment & EnterpriseSpecializationPageFieldsFragment)
        | null
        | undefined) ?? null;
  } else if (isProgramScoped) {
    loading = enterpriseResult.loading;
    error = enterpriseResult.error;
    pageData = enterpriseResult.data?.DescriptionPage?.queryEnterpriseSpecializationPage ?? null;
  } else {
    loading = consumerResult.loading;
    error = consumerResult.error;
    pageData = consumerResult.data?.DescriptionPage?.querySpecializationPage ?? null;
  }

  return {
    loading: !!slug && loading,
    error,
    notFound: !loading && !!slug && !pageData,
    pageData,
    isEnterprise,
  };
}
